import styled from 'styled-components';

import moment from 'moment-timezone';
import {
  displayDate,
  displayDateWithTodayAndYesterdayPrefix,
  DATE_FORMAT_WITH_COMMA_HOUR_MINS,
} from 'utils/date';

import { Avatar, AVATAR_SIZES } from 'ui/avatar';
import { Tooltip } from 'ui/tooltip';
import { spacing, body2, caption, colors } from 'ui/theme';

const displayText = {
  created: 'Created ',
  by: 'by ',
};

const Cell = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-start;
  overflow: hidden;
`;

const CellContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.base};
`;

const CellContentText = styled.div`
  display: flex;
  flex-direction: column;
`;

const CellContentTextHeader = styled.span`
  ${body2}
`;

const CellContentTextCaption = styled.span`
  ${caption}
  color: ${colors.textSecondary};
`;

type CreatedTooltipContentProps = {
  name: string;
  date: string | number;
};

export const CreatedTooltipContent = ({ date, name }: CreatedTooltipContentProps) => (
  <>
    {displayDate(date, DATE_FORMAT_WITH_COMMA_HOUR_MINS)}
    <br />
    {displayText.by}
    {name}
  </>
);

type AvatarTooltipContentProps = {
  email: string;
  name: string;
};

const AvatarTooltipContent = ({ email, name }: AvatarTooltipContentProps) =>
  name.length ? (
    <>
      {name}
      <br />
      {email}
    </>
  ) : (
    <>{email}</>
  );

export type UserInfo = {
  emailAddress: string;
  fullName?: string;
  avatarUrl?: string;
};

export type LastModifiedCellProps = {
  createdAt: string | number;
  createdBy: UserInfo;
  modifiedAt?: string | number | null;
  modifiedBy?: UserInfo;
  showAvatar?: boolean;
};

export const LastModifiedCell = ({
  createdBy,
  createdAt,
  modifiedBy,
  modifiedAt,
  showAvatar = true,
  ...otherProps
}: LastModifiedCellProps) => {
  const modifier = modifiedBy || createdBy;
  const [modifierFirstName, modifierLastName] = modifier.fullName?.split(' ') || [
    modifier.emailAddress,
    '',
  ];

  return (
    <Cell {...otherProps}>
      <CellContent>
        {showAvatar && (
          <Tooltip
            tooltipContent={
              <AvatarTooltipContent email={modifier.emailAddress} name={modifier.fullName || ''} />
            }
          >
            <Avatar
              size={AVATAR_SIZES.MEDIUM}
              firstName={modifierFirstName}
              lastName={modifierLastName}
              imgSrc={modifier.avatarUrl}
            />
          </Tooltip>
        )}
        <CellContentText>
          <CellContentTextHeader>
            {displayDateWithTodayAndYesterdayPrefix(modifiedAt || createdAt)}
          </CellContentTextHeader>
          <CellContentTextCaption>
            {displayText.created}
            <Tooltip
              isUnderlined
              tooltipContent={
                <CreatedTooltipContent
                  date={createdAt}
                  name={createdBy.fullName || createdBy.emailAddress}
                />
              }
            >
              {moment(createdAt).fromNow()}
            </Tooltip>
          </CellContentTextCaption>
        </CellContentText>
      </CellContent>
    </Cell>
  );
};
